import React from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Theme, Typography } from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/styles';
import { DateTime } from 'luxon';
import { difference, min } from 'lodash';

import { useUser } from '../../../global/auth/useUser';
import { useCampfireQuery } from '../../../global/network/useCampfireQuery';
import {
  ActivityAvailabilityGetActivity,
  ActivityAvailabilityGetActivityVariables,
  ActivityAvailabilityGetActivity_vm_activity as ActivityType,
} from './__generated__/ActivityAvailabilityGetActivity';
import {
  ActivityAvailabilityGetActivityEnrolment,
  ActivityAvailabilityGetActivityEnrolmentVariables,
} from './__generated__/ActivityAvailabilityGetActivityEnrolment';
import { ACTIVITY_AVAILABILITY_ACTIVITY_ENROLMENT } from './activity-availability-activity-enrolment-model.gql';
import { ACTIVITY_AVAILABILITY_ACTIVITY } from './activity-availability-activity-model.gql';
import { AvailabilityHeader } from '../AvailabilityHeader';
import { SearchField } from '../../inputs/SearchField';
import { SessionSelect } from '../SessionSelect';
import { DateAndFrequencySelect } from '../DateSelect';
import { DateSelect } from '../DateSelect/DateSelect';
import { AlertCard } from '../../cards/alert-card/AlertCard';
import { useCampfireTheme } from '../../../theme/useCampfireTheme';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    actionContainer: {
      paddingTop: '20px',
      paddingBottom: '30px',
      paddingRight: '22px',
    },
    toolbarButton: {
      borderRadius: '8px',
    },
    toolbarButtonSelected: {
      backgroundColor: theme.color.grey.neutral200,
      color: 'white',
      '&:hover': {
        opacity: 0.8,
        backgroundColor: theme.color.grey.neutral200,
      },
    },
    actionButton: {
      textTransform: 'capitalize',
    },
  })
);

interface ActivityAvailabilityProps {
  activityId: string;
  selectedDate: string;
  open: boolean;
  onClose: () => void;
  onSubmit: (selectedDates: string[], sessionIds: string[]) => void;
  availabilityRestrictionDays?: number | null;
}

enum AvailaibiltyStepEnum {
  SELECT_SESSIONS,
  SELECT_DATES,
}

export function ActivityAvailability({
  activityId,
  onClose,
  onSubmit,
  open,
  selectedDate,
  availabilityRestrictionDays,
}: ActivityAvailabilityProps) {
  const { getVolunteerIdentity } = useUser();
  const { volunteerId } = getVolunteerIdentity();
  const { theme } = useCampfireTheme();

  const { data: enrolmentData } = useCampfireQuery<
    ActivityAvailabilityGetActivityEnrolment,
    ActivityAvailabilityGetActivityEnrolmentVariables
  >(ACTIVITY_AVAILABILITY_ACTIVITY_ENROLMENT, {
    options: {
      variables: {
        volunteerId,
        activityId,
      },
    },
  });

  const { data: activityData } = useCampfireQuery<
    ActivityAvailabilityGetActivity,
    ActivityAvailabilityGetActivityVariables
  >(ACTIVITY_AVAILABILITY_ACTIVITY, {
    options: {
      variables: {
        activityId,
        from: DateTime.local().toISODate(),
        until: DateTime.local()
          .plus({ years: 1 })
          .toISODate(),
      },
    },
  });

  const activity = activityData?.vm.activity || ({} as ActivityType);

  const volunteerActivityRoleIds = React.useMemo(() => {
    return enrolmentData?.vm.volunteer?.activityRoles?.map((r: { activityRoleId: string }) => r.activityRoleId) ?? [];
  }, [enrolmentData]);

  const [step, setStep] = React.useState(AvailaibiltyStepEnum.SELECT_SESSIONS);
  const [searchValue, setSearchValue] = React.useState('');
  const handleCancel = () => {
    onClose();
  };
  const [value, setValue] = React.useState({
    sessionIds: [] as string[],
    dates: [selectedDate] as string[],
  });

  const filterSessions = React.useMemo(() => {
    return (
      activity.sessions?.filter((session) => {
        // Filter by search text
        const matchesSearch = session.name.toLowerCase().includes(searchValue.toLowerCase());

        // Filter out one-off sessions that don't match the selected date
        const matchesDate = !session.isOneOff || session.activityDate === selectedDate;

        // Filter out sessions that require roles the volunteer doesn't have
        const { requiredRoles } = session as any;
        const matchesRole =
          !requiredRoles ||
          requiredRoles.length === 0 ||
          requiredRoles.some((role: { activityRoleId: string }) =>
            volunteerActivityRoleIds.includes(role.activityRoleId)
          );

        return matchesSearch && matchesDate && matchesRole;
      }) || []
    );
  }, [activity.sessions, searchValue, selectedDate, volunteerActivityRoleIds]);

  const onSelectSession = (selectedSessionIds: string[]) => {
    setValue({
      ...value,
      sessionIds: selectedSessionIds,
    });
  };

  const onSelectDates = (selectedDates: string[]) => {
    setValue({
      ...value,
      dates: selectedDates,
    });
  };

  const recurrences = React.useMemo(() => {
    switch (activity.__typename) {
      case 'VOLUNTEER_RecurringActivityType':
        return activity.schedule.recurrences;
      default:
        return [];
    }
  }, [activity]);

  const classes = useStyles();

  const { availabilities, unavailabilities } = enrolmentData?.vm.volunteer?.activityEnrolment || {};
  const volunteerUnavailabilities = enrolmentData?.vm.volunteer?.unavailabilities || [];

  // Check if any selected dates overlap with volunteer leave periods
  const overlappingLeaveDates = React.useMemo(() => {
    if (!value.dates?.length || !volunteerUnavailabilities.length) return [];

    return value.dates.filter((date) => {
      const dateToCheck = DateTime.fromISO(date);
      return volunteerUnavailabilities.some((leave) => {
        const startDate = DateTime.fromISO(leave.startDate);
        const endDate = DateTime.fromISO(leave.endDate);
        return dateToCheck >= startDate && dateToCheck <= endDate;
      });
    });
  }, [value.dates, volunteerUnavailabilities]);

  const onBack = () => setStep(Math.max(step - 1, 0));
  const isNextEnabled = (() => {
    if (step === AvailaibiltyStepEnum.SELECT_SESSIONS) {
      return Boolean(value.sessionIds?.length);
    }
    return Boolean(value.dates?.length);
  })();

  // Check if any of the selected sessions are flexible sessions
  const hasSelectedFlexibleSession = React.useMemo(() => {
    if (!value.sessionIds?.length || !activity.sessions) return false;

    // Find all selected sessions that are flexible
    const selectedSessions = activity.sessions.filter((session) => value.sessionIds.includes(session.sessionId));

    // Check if any selected session is flexible and has instances for the selected date
    return selectedSessions.some((session) => {
      if (!session.__typename) return false;

      // Check if the session has the isFlexible property
      const { isFlexible } = session;

      // Check if the session has currentFlexibleInstances for the selected date
      const { currentFlexibleInstances = [] } = session;
      const hasFlexibleInstancesForDate = currentFlexibleInstances.some(
        (instance) => !instance.sessionDate || instance.sessionDate === selectedDate
      );

      return isFlexible && hasFlexibleInstancesForDate;
    });
  }, [value.sessionIds, activity.sessions, selectedDate]);

  const handleNext = () => {
    if (step === AvailaibiltyStepEnum.SELECT_DATES) {
      onSubmit(value.dates, value.sessionIds);
    } else if (step === AvailaibiltyStepEnum.SELECT_SESSIONS && hasSelectedFlexibleSession) {
      // Skip date selection for flexible sessions with instances on the selected date
      onSubmit([selectedDate], value.sessionIds);
    } else {
      setStep(Math.min(step + 1, AvailaibiltyStepEnum.SELECT_DATES));
    }
  };

  const dateAvailabilities = availabilities?.map((availability) => availability.activityDate) || [];
  const dateUnavailabilities = unavailabilities?.map((unavailability) => unavailability.activityDate) || [];
  const occurrences = activity.occurrencesBetween || [];
  const selectableOccurrences = difference(occurrences, dateAvailabilities);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='md'>
      <DialogTitle>
        <AvailabilityHeader
          header='Availability Centre'
          subHeader={step === AvailaibiltyStepEnum.SELECT_SESSIONS ? 'Select Sessions/s' : 'Select Dates'}
        >
          <Box display={'flex'} alignItems='center'>
            {step === AvailaibiltyStepEnum.SELECT_SESSIONS && (
              <SearchField
                value={searchValue}
                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                  setSearchValue(e.target.value)
                }
              />
            )}
          </Box>
        </AvailabilityHeader>
      </DialogTitle>
      <DialogContent>
        <Box>
          {/* Show warning if selected dates overlap with leave periods */}
          {step === AvailaibiltyStepEnum.SELECT_DATES && overlappingLeaveDates.length > 0 && (
            <Box marginBottom={2}>
              <AlertCard variant='warning' title='Leave Period Conflict'>
                <Typography
                  style={{ fontSize: '14px', marginTop: '4px', lineHeight: 1.5, color: theme.alert.yellow.medium }}
                >
                  You have selected dates that overlap with your leave periods (
                  {overlappingLeaveDates.map((d) => DateTime.fromISO(d).toFormat('dd/MM/yyyy')).join(', ')}).
                  <br />
                  <br />
                  Submitting availability for these dates will override your leave and mark you as available for
                  rostering. You can manage your leave periods in the Availability Centre by selecting &apos;Set
                  Unavailability&apos;.
                </Typography>
              </AlertCard>
            </Box>
          )}
          {(() => {
            switch (step) {
              case AvailaibiltyStepEnum.SELECT_SESSIONS:
                return (
                  <SessionSelect
                    selectedSessionIds={value.sessionIds || []}
                    sessions={filterSessions}
                    onSelectSession={onSelectSession}
                    currentDate={selectedDate}
                  />
                );
              case AvailaibiltyStepEnum.SELECT_DATES:
                return recurrences.length > 0 ? (
                  <DateAndFrequencySelect
                    occurrences={selectableOccurrences}
                    recurrences={recurrences}
                    value={value.dates || []}
                    onChange={onSelectDates}
                    key={activity.activityId}
                    availabilities={dateAvailabilities}
                    unavailabilities={dateUnavailabilities}
                    startDate={min([activity.startDate, selectableOccurrences[0]])}
                    availabilityRestrictionDays={availabilityRestrictionDays}
                  />
                ) : (
                  <DateSelect
                    multiple
                    value={value.dates || []}
                    occurrences={selectableOccurrences}
                    availabilities={dateAvailabilities}
                    unavailabilities={dateUnavailabilities}
                    startDate={min([activity.startDate, selectableOccurrences[0]])}
                    onChange={onSelectDates}
                    availabilityRestrictionDays={availabilityRestrictionDays}
                  />
                );
              default:
                return null;
            }
          })()}
        </Box>
      </DialogContent>
      <DialogActions className={classes.actionContainer}>
        {step === AvailaibiltyStepEnum.SELECT_SESSIONS ? (
          <Button onClick={handleCancel} disableElevation className={classes.actionButton}>
            Cancel
          </Button>
        ) : (
          <Button onClick={onBack} disableElevation className={classes.actionButton}>
            Back
          </Button>
        )}
        <Button
          onClick={handleNext}
          disableElevation
          variant='contained'
          color='primary'
          disabled={!isNextEnabled}
          className={classes.actionButton}
        >
          {step === AvailaibiltyStepEnum.SELECT_DATES ? 'Submit' : 'Next'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
